import { useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { Text, Button, Separator } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import { usePrevious, useReselector } from '@/utils/sharedHooks'
import { fetchTransactionsByCategoryId } from '@/actions/admin/transactionActions'
import { fetchAdminJournalEntryLines } from '@/actions/reportActionsV2'
import { selectJournalEntryLineIdsByCategoryId } from '@/reducers/admin/journalEntryLineReducer'

import { LineItemTransaction } from './LineItemTransaction'
import { LineItemJournalEntry } from './LineItemJournalEntry'
import { getUserIsAdmin } from '@/selectors/user.selectors'
import { TransactionsByCategoryId } from '@/v2-features/transactions/transactions.slice'
import { TRANSACTION_CATEGORY_IDENTIFIERS } from '@/v2-features/transactions/constants'
import { getTransactionCategoryIdentifierSelector } from '../reports.selectors'

const LineItemExpanded = ({
  item,
  startDate,
  endDate,
  open,
  userIdForAdmin,
  confirmed,
}: {
  item: { sum: number | string; transactionCategoryId: number | null }
  startDate: string | undefined
  endDate: string | undefined
  open: boolean
  userIdForAdmin: number | null
  confirmed?: boolean
}) => {
  const dispatch = useAppDispatch()
  const [viewAllTransactions, setViewAllTransactions] = useState(false)
  const [viewedTransactions, setViewedTransactions] = useState<
    TransactionsByCategoryId['transactions']
  >([])
  const [viewedJournalEntryIds, setViewedJournalEntryIds] = useState<number[]>(
    []
  )
  const [hasBeenFetched, setHasBeenFetched] = useState(false)
  const previousStartDate = usePrevious(startDate)
  const previousEndDate = usePrevious(endDate)
  const isAdmin = useReselector(getUserIsAdmin)

  const categoryIdentifier = useReselector(
    getTransactionCategoryIdentifierSelector,
    item.transactionCategoryId
  )

  const isOwnersCategory =
    categoryIdentifier ===
      TRANSACTION_CATEGORY_IDENTIFIERS.ownersDistribution ||
    categoryIdentifier === TRANSACTION_CATEGORY_IDENTIFIERS.ownersInvestments

  useEffect(() => {
    if (previousStartDate !== startDate || endDate !== previousEndDate) {
      setHasBeenFetched(false)
    }
  }, [startDate, endDate, previousStartDate, previousEndDate])

  const [categoryTransactions, setCategoryTransactions] =
    useState<TransactionsByCategoryId>({
      transactionCategoryId: item.transactionCategoryId || 0,
      transactions: [],
      count: 0,
      userId: userIdForAdmin || undefined,
    })

  const categoryTransactionIds = useMemo(
    () =>
      categoryTransactions.transactions.map((transaction) => transaction.id),
    [categoryTransactions.transactions]
  )

  const numOfTransactions = useMemo(
    () => categoryTransactionIds.length,
    [categoryTransactionIds]
  )

  const journalEntriesByCategory = useReselector(
    selectJournalEntryLineIdsByCategoryId,
    startDate,
    endDate,
    item.transactionCategoryId
  )

  useEffect(() => {
    const fetch = async () => {
      if (open && !hasBeenFetched) {
        const transactions = await fetchTransactionsByCategoryId({
          id: item.transactionCategoryId,
          limit: null,
          startDate: startDate
            ? DateTime.fromFormat(startDate, DATE_FORMATS_LUXON.API_INPUT)
            : undefined,
          endDate: endDate
            ? DateTime.fromFormat(endDate, DATE_FORMATS_LUXON.API_INPUT)
            : undefined,
          userId: userIdForAdmin,
        })(dispatch)
        if (transactions) {
          setCategoryTransactions(transactions)
        }

        if (isAdmin) {
          fetchAdminJournalEntryLines({
            startDate: startDate
              ? DateTime.fromFormat(startDate, DATE_FORMATS_LUXON.API_INPUT)
              : DateTime.fromISO('2023-01-01'),
            endDate: endDate
              ? DateTime.fromFormat(endDate, DATE_FORMATS_LUXON.API_INPUT)
              : DateTime.fromISO('2023-12-31'),
            userId: Number(userIdForAdmin),
            transactionCategoryId: Number(item.transactionCategoryId),
          })(dispatch)
        }

        setHasBeenFetched(true)
      }
    }

    fetch()
  }, [
    item.transactionCategoryId,
    dispatch,
    endDate,
    startDate,
    open,
    hasBeenFetched,
    userIdForAdmin,
    isAdmin,
  ])

  useEffect(() => {
    if (confirmed) {
      if (!journalEntriesByCategory.length) {
        return
      }
      if (viewAllTransactions) {
        setViewedJournalEntryIds(journalEntriesByCategory)
      } else {
        setViewedJournalEntryIds(journalEntriesByCategory.slice(0, 10))
      }
    } else {
      if (numOfTransactions === 0) {
        return
      }
      if (viewAllTransactions) {
        setViewedTransactions(categoryTransactions.transactions)
      } else {
        setViewedTransactions(categoryTransactions.transactions.slice(0, 10))
      }
    }
  }, [
    viewAllTransactions,
    categoryTransactionIds,
    journalEntriesByCategory,
    numOfTransactions,
    confirmed,
    categoryTransactions.transactions,
  ])

  if (!open || (!viewedTransactions.length && !viewedJournalEntryIds.length)) {
    return null
  }

  if (confirmed) {
    return (
      <div className="flex flex-col gap-4 p-2">
        <Separator />

        {viewedJournalEntryIds.map((id) => (
          <LineItemJournalEntry key={id} journalEntryId={id} />
        ))}

        <div className="flex flex-wrap items-center">
          {numOfTransactions > 0 && (
            <Text
              color={TextColors.MUTED}
              variant={TypographyStyles.BODY_SM}
              className="w-full md:w-1/2"
            >
              Showing {viewedJournalEntryIds.length} of&nbsp;
              {journalEntriesByCategory.length}
            </Text>
          )}
          {numOfTransactions > 10 && (
            <Button
              variant="link"
              onClick={() => setViewAllTransactions((prev) => !prev)}
              className="w-full md:w-1/2 flex justify-end"
            >
              {viewAllTransactions
                ? 'View fewer journal entries'
                : 'View all journal entries'}
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-2">
      <Separator color={TextColors.MUTED} />

      {viewedTransactions.map((transaction) => (
        <LineItemTransaction
          key={transaction.id}
          transaction={transaction}
          showManualUploadTooltip={
            isOwnersCategory && Boolean(transaction.isManualUpload)
          }
        />
      ))}

      <div className="flex flex-wrap items-center">
        {Boolean(numOfTransactions) && (
          <div className="w-full md:w-1/2">
            <Text color={TextColors.MUTED}>
              Showing {viewedTransactions.length} of&nbsp;
              {categoryTransactions.count}
            </Text>
          </div>
        )}
        {numOfTransactions > 10 && (
          <Button
            variant="link"
            onClick={() => setViewAllTransactions((prev) => !prev)}
            className="w-full md:w-1/2 flex justify-end"
          >
            {viewAllTransactions
              ? 'View fewer transactions'
              : 'View all transactions'}
          </Button>
        )}
      </div>
    </div>
  )
}

export { LineItemExpanded }
